import { Box, BoxProps } from '@material-ui/core';
import { Currency, formatMoney, formatNumber } from '@nemuru-eng/js-api-client';
import { generatePath } from 'react-router';
import { Link } from 'react-router-dom';

import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { Text } from '@/components/shared/components/Text';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { getIconName } from '@/utils/getIconName';

import { HorizontalScoreCard } from '../../shared/components/HorizontalScoreCard';
import { MetricVariation } from '../../shared/components/MetricVariation';
import { ScoreCardMetricKey } from '../../shared/types/ScoreCardMetricKey';
import { ChannelScoreCardMetrics } from '../types/AgentScoreCardMetrics';
import { MetricsHorizontalProgress } from './MetricsHorizontalProgress';

interface ChannelScoreCardProps extends BoxProps {
  currency: Currency;
  metric: ChannelScoreCardMetrics;
  selectedChannelId: undefined | string;
  setSelectedChannelId: React.Dispatch<React.SetStateAction<string | undefined>>;
  selectedMetricsType: ScoreCardMetricKey;
  maxChannelValues: ChannelScoreCardMetrics;
  setPage?: React.Dispatch<React.SetStateAction<number>>;
}

export const ChannelScoreCard = ({
  currency,
  metric,
  setSelectedChannelId,
  selectedChannelId,
  selectedMetricsType,
  maxChannelValues,
  setPage,
  ...props
}: ChannelScoreCardProps) => {
  const { palette } = useCustomTheme();

  const handleOnClick = (channelId) => {
    if (channelId === selectedChannelId) {
      setSelectedChannelId(undefined);
      return;
    }
    if (setPage) setPage(1);
    setSelectedChannelId(channelId);
  };

  const isChannelNotSelected = selectedChannelId && selectedChannelId !== metric.channelId;
  const isChannelSelected = selectedChannelId && selectedChannelId === metric.channelId;

  return (
    <HorizontalScoreCard
      whiteSpace={'nowrap'}
      maxHeight={'68px'}
      mb={2}
      icon={
        <Box
          width={24}
          height={40}
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
          color={'secondary.main'}
        >
          <Font variant="h5">
            <Icon>{getIconName(metric.channelType)}</Icon>
          </Font>
        </Box>
      }
      title={
        <>
          {metric.channelDescription}
          <Link
            style={{ display: 'inline-flex', marginLeft: 6, position: 'relative', top: '1px' }}
            to={generatePath(DYNAMIC_PAGE_PATHS.CHANNEL, {
              merchantId: metric.merchantId,
              channelId: metric.channelId,
            })}
          >
            <Icon size={14}>open_in_new</Icon>
          </Link>
        </>
      }
      subtitle={
        <Text
          tId={'OF_PREV_PERIOD'}
          dynamicDataTexts={[
            selectedMetricsType === ScoreCardMetricKey.totalPrincipalAmount
              ? formatMoney({ value: metric[selectedMetricsType].previous, currency }, 0)
              : formatNumber(metric[selectedMetricsType].previous, 0),
          ]}
        />
      }
      value={
        selectedMetricsType === ScoreCardMetricKey.totalPrincipalAmount
          ? formatMoney({ value: metric[selectedMetricsType].current, currency }, 0)
          : formatNumber(metric[selectedMetricsType].current, 0)
      }
      subValue={<MetricVariation variation={metric[selectedMetricsType].variation} />}
      onClick={() => handleOnClick(metric.channelId)}
      style={{
        cursor: 'pointer',
        opacity: isChannelNotSelected ? '0.33' : '1',
        backgroundColor: isChannelSelected ? palette.minor.main : undefined,
        transition: 'all 0.2s ease',
      }}
      hideDivider
      {...props}
    >
      <MetricsHorizontalProgress
        metric={metric}
        maxMetricsValues={maxChannelValues}
        selectedMetricsType={selectedMetricsType}
      />
    </HorizontalScoreCard>
  );
};
